/**
 * Suppliers, their conditions and their tariffs (contract v10.48, ADR 0043 of the back).
 *
 * In `shared/` and not in a feature because two screens load a tariff: «Documentos», and
 * the detail of an email whose attachment is one («Cargar como tarifa»).
 */
import { api, type Page } from '@/shared/api/client';

import type {
  ColumnsView,
  ColumnsWrite,
  FromAttachment,
  OriginalLink,
  PublishRequest,
  Supplier,
  SupplierConditionWrite,
  SupplierUpdate,
  SupplierWrite,
  TariffItem,
  TariffSearchResult,
  Version,
  VersionBrief,
} from './types';

export interface TariffSearchParams {
  q?: string;
  supplier_id?: string;
  family?: string;
  limit?: number;
}

/** The most the API gives in one page: «Documentos» pages past it, the pickers search. */
export const SUPPLIERS_PAGE = 200;

export const tariffKeys = {
  all: ['tariffs'] as const,
  suppliers: ['tariffs', 'suppliers'] as const,
  supplierList: (q: string, offset: number) => ['tariffs', 'suppliers', 'list', q, offset] as const,
  supplier: (id: string) => ['tariffs', 'suppliers', 'detail', id] as const,
  search: (params: TariffSearchParams) => ['tariffs', 'search', params] as const,
  families: (supplierId: string | undefined) => ['tariffs', 'families', supplierId ?? ''] as const,
  version: (id: string) => ['tariffs', 'version', id] as const,
  columns: (id: string, sheet: string, headerRow: number) =>
    ['tariffs', 'version', id, 'columns', sheet, headerRow] as const,
  items: (id: string, offset: number) => ['tariffs', 'version', id, 'items', offset] as const,
  history: (listId: string) => ['tariffs', 'history', listId] as const,
};

function fileForm(file: File, extra: Record<string, string> = {}): FormData {
  const form = new FormData();
  form.append('file', file);
  for (const [key, value] of Object.entries(extra)) form.append(key, value);
  return form;
}

export const tariffsApi = {
  suppliers: (q = '', offset = 0): Promise<Page<Supplier>> =>
    api.get<Page<Supplier>>('/api/suppliers', { q, limit: SUPPLIERS_PAGE, offset }),

  supplier: (id: string): Promise<Supplier> => api.get<Supplier>(`/api/suppliers/${id}`),

  createSupplier: (payload: SupplierWrite): Promise<Supplier> =>
    api.post<Supplier>('/api/suppliers', payload),

  updateSupplier: (id: string, payload: SupplierUpdate): Promise<Supplier> =>
    api.patch<Supplier>(`/api/suppliers/${id}`, payload),

  /** Every condition at once: what is not in the list is removed. */
  putConditions: (id: string, conditions: SupplierConditionWrite[]): Promise<Supplier> =>
    api.put<Supplier>(`/api/suppliers/${id}/conditions`, { conditions }),

  /** A new tariff of a supplier, with its first file. */
  createPriceList: (supplierId: string, name: string, file: File): Promise<Version> =>
    api.postForm<Version>(`/api/suppliers/${supplierId}/price-lists`, fileForm(file, { name })),

  /** A new version of a tariff that exists. 409 while another one is on its way. */
  uploadVersion: (priceListId: string, file: File): Promise<Version> =>
    api.postForm<Version>(`/api/tariffs/lists/${priceListId}/versions`, fileForm(file)),

  fromAttachment: (payload: FromAttachment): Promise<Version> =>
    api.post<Version>('/api/tariffs/from-attachment', payload),

  history: (priceListId: string): Promise<Page<VersionBrief>> =>
    api.get<Page<VersionBrief>>(`/api/tariffs/lists/${priceListId}/versions`, { limit: 50 }),

  version: (id: string): Promise<Version> => api.get<Version>(`/api/tariffs/versions/${id}`),

  /** The titles and twenty rows of another sheet or another row of titles. */
  columns: (id: string, sheet: string, headerRow: number): Promise<ColumnsView> =>
    api.get<ColumnsView>(`/api/tariffs/versions/${id}/columns`, {
      sheet,
      header_row: headerRow,
    }),

  confirmColumns: (id: string, payload: ColumnsWrite): Promise<Version> =>
    api.put<Version>(`/api/tariffs/versions/${id}/columns`, payload),

  /** 409 `salespeople_email`: the email to «Comerciales» cannot go, and nothing was
   * published (contract v10.50). */
  publish: (id: string, payload: PublishRequest): Promise<Version> =>
    api.post<Version>(`/api/tariffs/versions/${id}/publish`, payload),

  discard: (id: string): Promise<Version> =>
    api.post<Version>(`/api/tariffs/versions/${id}/discard`),

  /** Ask the worker again: a version stuck in its hands, or one the queue refused. */
  retry: (id: string): Promise<Version> => api.post<Version>(`/api/tariffs/versions/${id}/retry`),

  items: (id: string, offset: number, limit = 50): Promise<Page<TariffItem>> =>
    api.get<Page<TariffItem>>(`/api/tariffs/versions/${id}/items`, { limit, offset }),

  original: (id: string): Promise<OriginalLink> =>
    api.get<OriginalLink>(`/api/tariffs/versions/${id}/original`),

  search: (params: TariffSearchParams): Promise<TariffSearchResult> =>
    api.get<TariffSearchResult>('/api/tariffs/search', { ...params }),

  families: (supplierId?: string): Promise<string[]> =>
    api.get<string[]>('/api/tariffs/families', { supplier_id: supplierId }),
};
